import type { TankDTO } from "../../../shared/protocol/index.js";
import { state } from "../../state.js";
import type { Renderer } from "../Renderer.js";
import { REDUCED_MOTION } from "../types.js";
import { drawWindupStubs } from "../scope.js";
import { hexToRgba } from "../util.js";
import { drawLocalIndicator } from "./indicator.js";

/** Modern 4K neon theme. Called inside drawTank's save/translate/alpha. */
export function drawModernTank(rd: Renderer, t: TankDTO, isLocal: boolean, nowMs: number): void {
  const { ctx } = rd;
  const r = rd.tankR;

  // Hover shadow + under-glow (before body rotation and altitude bob)
  if (t.alive) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(0, 0, r * 1.1, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
    ctx.fill();

    ctx.beginPath();
    ctx.arc(0, 0, r * 0.95, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0, 248, 248, 0.10)";
    ctx.fill();
    ctx.restore();
  }

  // Altitude bob: ±1px vertical offset screen-space
  const idHash = t.id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const bobY = REDUCED_MOTION ? 0 : Math.sin(nowMs / 300 + idHash) * 1.0;
  if (bobY !== 0) {
    ctx.translate(0, bobY);
  }

  // Local-player facing indicator: a glowing sky-blue arrow ahead of your tank.
  if (isLocal && t.alive) {
    drawLocalIndicator(ctx, r, t.bodyAngle, nowMs);
  }

  ctx.save();
  ctx.rotate(t.bodyAngle);

  // Determine neon colors
  // Self/teammates = cyan (#00f8f8), enemies = hot pink/rose (#f43f5e)
  let neonColor = "#f43f5e";
  if (isLocal) {
    neonColor = "#00f8f8";
  } else if (t.team !== -1) {
    const localTeam = rd.displayedSnap?.tanks.find(tk => tk.id === state.playerId)?.team;
    if (t.team === localTeam) {
      neonColor = "#00f8f8";
    }
  }

  // Resolve modern tank styles
  let style = state.modernStyle;
  if (!isLocal) {
    const hash = t.id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const styles = ["railgun", "hover", "plasma", "siege"] as const;
    style = styles[hash % styles.length];
  }

  // Draw Chassis based on futuristic style
  if (style === "hover") {
    // Hover pods (no tracks)
    ctx.fillStyle = "#1e293b";
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    ctx.fillStyle = neonColor;
    ctx.beginPath();
    ctx.arc(-r * 0.5, -r * 0.5, r * 0.22, 0, Math.PI * 2);
    ctx.arc(-r * 0.5, r * 0.5, r * 0.22, 0, Math.PI * 2);
    ctx.arc(r * 0.5, 0, r * 0.22, 0, Math.PI * 2);
    ctx.fill();
  } else if (style === "plasma") {
    // Hexagonal energy chassis
    ctx.fillStyle = "#0f172a";
    ctx.beginPath();
    ctx.moveTo(-r, -r * 0.55);
    ctx.lineTo(-r * 0.5, -r);
    ctx.lineTo(r * 0.5, -r);
    ctx.lineTo(r, -r * 0.55);
    ctx.lineTo(r, r * 0.55);
    ctx.lineTo(r * 0.5, r);
    ctx.lineTo(-r * 0.5, r);
    ctx.lineTo(-r, r * 0.55);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = neonColor;
    ctx.lineWidth = 2;
    ctx.stroke();
  } else if (style === "siege") {
    // Heavy armor plates
    ctx.fillStyle = "#020617";
    ctx.fillRect(-r * 1.1, -r * 0.9, r * 2.2, r * 1.8);
    ctx.strokeStyle = "rgba(255,255,255,0.1)";
    ctx.strokeRect(-r * 1.1, -r * 0.9, r * 2.2, r * 1.8);

    // Front & back treads
    ctx.fillStyle = "#1e293b";
    ctx.fillRect(-r * 1.25, -r * 1.05, r * 0.7, r * 0.35);
    ctx.fillRect(-r * 1.25, r * 0.7, r * 0.7, r * 0.35);
    ctx.fillRect(r * 0.55, -r * 1.05, r * 0.7, r * 0.35);
    ctx.fillRect(r * 0.55, r * 0.7, r * 0.7, r * 0.35);
  } else {
    // Railgun: sleek carbon wing plates
    ctx.fillStyle = "#0f172a";
    ctx.beginPath();
    ctx.moveTo(-r * 1.1, -r * 0.8);
    ctx.lineTo(-r * 0.4, -r * 0.8);
    ctx.lineTo(r * 0.8, -r * 0.4);
    ctx.lineTo(r * 0.8, r * 0.4);
    ctx.lineTo(-r * 0.4, r * 0.8);
    ctx.lineTo(-r * 1.1, r * 0.8);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    ctx.fillStyle = "#000000";
    ctx.fillRect(-r * 0.95, -r * 0.95, r * 1.9, r * 0.22);
    ctx.fillRect(-r * 0.95, r * 0.73, r * 1.9, r * 0.22);
  }

  // Glowing power core
  const corePulse = 0.8 + 0.2 * Math.abs(Math.sin(nowMs / 180));
  const grad = ctx.createRadialGradient(0, 0, 1, 0, 0, r * 0.45);
  grad.addColorStop(0, "#ffffff");
  grad.addColorStop(0.3, neonColor);
  grad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.45 * corePulse, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore(); // End body rotation

  // Turret + barrel
  ctx.save();
  ctx.rotate(t.turretAngle);

  if (style === "hover") {
    // Dual energy rail capacitor barrel
    ctx.fillStyle = "#334155";
    ctx.fillRect(0, -4.5, r * 1.6, 3);
    ctx.fillRect(0, 1.5, r * 1.6, 3);
    ctx.fillStyle = neonColor;
    ctx.fillRect(r * 0.4, -1, r * 1.2, 2);

    ctx.beginPath();
    ctx.arc(0, 0, r * 0.45, 0, Math.PI * 2);
    ctx.fillStyle = "#1e293b";
    ctx.fill();
    ctx.strokeStyle = neonColor;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  } else if (style === "plasma") {
    // Plasma condenser rings barrel
    ctx.fillStyle = "#334155";
    ctx.fillRect(0, -3.5, r * 1.5, 7);
    ctx.fillStyle = neonColor;
    ctx.fillRect(r * 0.4, -4.5, r * 0.15, 9);
    ctx.fillRect(r * 0.8, -4.5, r * 0.15, 9);
    ctx.fillRect(r * 1.2, -4.5, r * 0.15, 9);

    ctx.beginPath();
    ctx.arc(0, 0, r * 0.5, 0, Math.PI * 2);
    ctx.fillStyle = "#0f172a";
    ctx.fill();
    ctx.strokeStyle = neonColor;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  } else if (style === "siege") {
    // Heavy artillery tube cannon
    ctx.fillStyle = "#1e293b";
    ctx.fillRect(0, -5, r * 1.9, 10);
    ctx.fillStyle = "#000000";
    ctx.fillRect(r * 1.9 - 2, -6, 4, 12);

    ctx.beginPath();
    ctx.arc(0, 0, r * 0.6, 0, Math.PI * 2);
    ctx.fillStyle = "#020617";
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.2)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  } else {
    // Railgun barrel
    ctx.fillStyle = "#334155";
    ctx.fillRect(0, -3, r * 1.7, 6);
    ctx.fillStyle = neonColor;
    ctx.fillRect(r * 0.2, -1, r * 1.4, 2);

    ctx.beginPath();
    ctx.arc(0, 0, r * 0.45, 0, Math.PI * 2);
    ctx.fillStyle = "#0f172a";
    ctx.fill();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  ctx.restore();

  // Laser windup
  if (t.charging && t.alive) {
    ctx.save();
    ctx.rotate(t.turretAngle);
    ctx.globalAlpha = 0.6 + 0.35 * Math.abs(Math.sin(nowMs / 40));
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2.5;
    drawWindupStubs(rd, ctx, r, t.weaponCharges);
    ctx.restore();
  }

  // Shield bubble
  if (t.shielded && t.alive) {
    ctx.save();
    const pulse = 0.9 + 0.1 * Math.sin(nowMs / 100);
    ctx.strokeStyle = neonColor;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, r * 1.5 * pulse, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = hexToRgba(neonColor, 0.05);
    ctx.fill();
    ctx.restore();
  }

  ctx.restore();

  // Health bar
  if (t.alive && t.maxHp > 1) {
    const bw = r * 2;
    const bx = t.x - r;
    const by = t.y - r - 6;
    const frac = Math.max(0, Math.min(1, t.hp / t.maxHp));
    ctx.fillStyle = "rgba(15, 23, 42, 0.6)";
    ctx.fillRect(bx, by, bw, 3);
    ctx.fillStyle = frac > 0.5 ? "#10b981" : frac > 0.25 ? "#f59e0b" : "#ef4444";
    ctx.fillRect(bx, by, bw * frac, 3);
  }

  // Name / respawn label
  ctx.font = "bold 11px 'Outfit', sans-serif";
  ctx.textAlign = "center";
  if (t.alive) {
    ctx.fillStyle = isLocal ? "#00f8f8" : "#f1f5f9";
    const above = t.y - r - (t.maxHp > 1 ? 13 : 8);
    const labelY = above < 10 ? t.y + r + 14 : above;
    ctx.fillText(t.name, t.x, labelY);
  } else {
    ctx.fillStyle = "#64748b";
    ctx.fillText(`${Math.ceil(t.respawnIn)}`, t.x, t.y + 4);
  }
}
